import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Ticket, Leaf, Lightbulb, Copy, Loader2, Plus, KeyRound } from "lucide-react";
import { api, apiError } from "../lib/api";

const STATUS = {
  unused: { label: "Disponible", cls: "border-green-500/40 text-green-400" },
  redeemed: { label: "En uso", cls: "border-yellow-400/40 text-yellow-300" },
  finished: { label: "Utilizado", cls: "border-zinc-600 text-zinc-400" },
};

const copyCode = (code) => {
  navigator.clipboard.writeText(code);
  toast.success(`PIN ${code} copiado`);
};

const PinRow = ({ pin }) => {
  const st = STATUS[pin.status] || STATUS.unused;
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2.5">
      <span className="font-mono text-sm tracking-wider">{pin.code}</span>
      <div className="flex items-center gap-2">
        <span className={`text-[11px] px-2 py-0.5 rounded-full border ${st.cls}`}>{st.label}</span>
        <button data-testid="copy-pin" onClick={() => copyCode(pin.code)} className="text-zinc-500 hover:text-white" aria-label="Copiar PIN">
          <Copy size={14} />
        </button>
      </div>
    </div>
  );
};

const PinPanel = ({ type, title, desc, icon: Icon, accent, pins, onGenerate, generating }) => {
  const [count, setCount] = useState(5);
  return (
    <div data-testid={`admin-pins-${type}`} className={`rounded-2xl border p-6 ${accent.panel}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={accent.icon} size={20} />
        <h3 className="font-display font-bold text-lg">{title}</h3>
      </div>
      <p className="text-xs text-zinc-400 mb-4">{desc}</p>
      <div className="flex items-center gap-2 mb-4">
        <select
          data-testid={`pin-count-${type}`}
          value={count}
          onChange={(e) => setCount(Number(e.target.value))}
          className="bg-black border border-white/15 rounded-xl px-3 py-2 text-sm"
        >
          {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <button
          data-testid={`generate-pins-${type}`}
          onClick={() => onGenerate(type, count)}
          disabled={generating === type}
          className={`inline-flex items-center gap-1.5 text-sm font-bold rounded-xl px-4 py-2 ${accent.button}`}
        >
          {generating === type ? <Loader2 className="animate-spin" size={15} /> : <Plus size={15} />} Generar {count} PIN{count > 1 ? "s" : ""}
        </button>
      </div>
      <div className="rounded-xl border border-white/10 bg-black/40 max-h-72 overflow-y-auto divide-y divide-white/5">
        {pins.length === 0 ? (
          <p className="text-zinc-500 text-sm p-4">Aún no hay PINs de este tipo.</p>
        ) : (
          pins.map((p) => <PinRow key={p.pin_id} pin={p} />)
        )}
      </div>
      <p className="text-[11px] text-zinc-500 mt-2">{pins.length} PIN(s) en total.</p>
    </div>
  );
};

export default function AdminPins() {
  const [pins, setPins] = useState({ standard: [], renalife: [], lumi: [] });
  const [generating, setGenerating] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/pins").then((r) => setPins(r.data)).catch((err) => console.error("Error cargando pins:", err));
  }, []);

  useEffect(() => { load(); }, [load]);

  const generate = async (type, count) => {
    setGenerating(type);
    try {
      await api.post("/admin/pins/generate", { pin_type: type, count });
      toast.success(`${count} PIN(s) ${type === "renalife" ? "RENALIFE" : type === "lumi" ? "LUMI" : "tradicionales"} generados`);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setGenerating(null);
    }
  };

  return (
    <div className="mt-12">
      <div className="flex items-center gap-2 mb-2">
        <KeyRound className="text-purple-400" size={22} />
        <h2 className="font-display font-bold text-xl">PINs de acceso</h2>
      </div>
      <p className="text-sm text-zinc-400 mb-5">
        Aquí generas los dos tipos de PIN. <b className="text-zinc-200">Tradicionales</b>: el invitado crea UNA campaña con la empresa que él elija.{" "}
        <b className="text-emerald-300">RENALIFE</b>: acceso exclusivo para distribuidores RENALIFE con todos los materiales precargados (empresa, productos, precios y marca) — no editan nada.
      </p>
      <div className="grid lg:grid-cols-2 gap-5">
        <PinPanel
          type="standard"
          title="PINs tradicionales (campaña)"
          desc="Mismos PINs que generan los usuarios Élite. Dan acceso a crear UNA campaña completa con cualquier empresa (1 texto, 3 imágenes, 1 video)."
          icon={Ticket}
          accent={{
            panel: "border-purple-500/30 bg-[#121212]",
            icon: "text-yellow-400",
            button: "bg-yellow-400 hover:bg-yellow-300 text-black",
          }}
          pins={pins.standard}
          onGenerate={generate}
          generating={generating}
        />
        <PinPanel
          type="renalife"
          title="PINs RENALIFE (distribuidores)"
          desc="Acceso a la zona RENALIFE MÉXICO: la plataforma ya trae estructurados los mensajes, imágenes, video, página de aterrizaje y calificación IA con la marca oficial. Mismos límites (1 texto, 3 imágenes, 1 video)."
          icon={Leaf}
          accent={{
            panel: "border-emerald-500/40 bg-[#04231b]",
            icon: "text-emerald-400",
            button: "bg-[#FACC15] hover:bg-yellow-300 text-[#022C22]",
          }}
          pins={pins.renalife}
          onGenerate={generate}
          generating={generating}
        />
        <PinPanel
          type="lumi"
          title="PINs LUMI (distribuidores)"
          desc="Acceso a la zona LUMI (tutoría educativa para niños de 6 a 14 años): incluye la Academia LUMI exclusiva, el ABC del Multinivel y todas las herramientas generales de la plataforma."
          icon={Lightbulb}
          accent={{
            panel: "border-sky-500/40 bg-[#0B1F3A]",
            icon: "text-sky-400",
            button: "bg-sky-500 hover:bg-sky-400 text-white",
          }}
          pins={pins.lumi || []}
          onGenerate={generate}
          generating={generating}
        />
      </div>
    </div>
  );
}
